import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { httpError } from './error';

/**
 * 静态文件 Axios 实例
 * 支持类型：1. 阿里云 OSS ; 2. 当前项目目录里的静态文件
 * 用来请求静态 json 文件，不需要后端服务支持
 */

type ReqType = 'static' | 'oss';
type InsMap = { [K in ReqType]: AxiosInstance };

// http 请求额外的参数
interface AxiosExtraParams extends AxiosRequestConfig {
  ignoreError?: boolean;  // 是否忽略错误提示
  ignoreCodes?: number[]; // 需要忽略的业务错误码(即使报错也会表示响应成功)
}

// 用来获取 `根目录/public` 下的 json 文件
// 编译后会把 `public` 目录下的文件直接放到根目录, 所以 baseUrl 使用 '/'
// 请求地址就只能限定为 `public` 目录路径(路径不需要包含 'public')
const pub = axios.create({
  baseURL: '/',
  timeout: 1000 * 5,
});

// 用来获取 oss 里的 json 文件
const oss = axios.create({
  baseURL: process.env.VUE_APP_RES_OSS_URL,
  timeout: 1000 * 5,
});

// pub 响应拦截器
pub.interceptors.response.use((response) => {
  return response.data.data;
}, (error) => {
  if (!error.config.ignoreError) {
    httpError(error);
  }
  return Promise.reject(error);
});

// oss 响应拦截器
oss.interceptors.response.use((response) => {
  return response.data;
}, (error) => {
  const httpCode = error.response?.status || -1;
  const ignoreCodes = error.config.ignoreCodes;

  if (ignoreCodes.indexOf(httpCode) > -1) {
    // 即使报了 http 错误，也会表示为响应成功
    // 比如：要用 promise.all 同时获取两个 json 文件, 其中一个出现了 404 错误, 另一个正常
    // 如果不忽略 404, 则 promise.all 直接进入 catch, 另一个正常的请求也会受到影响
    // 如果忽略 404, 则表示两个请求都响应成功了, promise.all 就会进入 then
    return Promise.resolve(null);
  } else {
    if (!error.config.ignoreError) {
      httpError(error);
    }
    return Promise.reject(error);
  }
});

/**
 * 以 get 方式获取静态文件
 * @param type        http实例类型，目前只提供了 'static' 和 'oss' 两种
 * @param url         请求地址
 * @param params      传递的自定义 header 参数
 */
export function asset(type: ReqType, url: string, params: AxiosExtraParams = {}): Promise<any> {
  const instanceMap: InsMap = { oss, static: pub };
  return instanceMap[type].get(`${url}?t=${Date.now()}`, params);
}
